import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Loader2, Bot, User, Minimize2, Maximize2, Calendar, MapPin, Star } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const absolute = (url) => (url && url.startsWith('/') ? `${BACKEND}${url}` : url);

/** Small card grid rendered inside an assistant bubble when backend returns provider_suggestions. */
const ProviderSuggestions = ({ action }) => {
  const { t } = useTranslation();
  if (!action || action.type !== 'provider_suggestions') return null;
  const { query, providers } = action;
  if (!providers || providers.length === 0) return null;

  const openBooking = (url) => {
    // Preserve SPA navigation if possible; fall back to plain nav
    try {
      window.location.assign(url);
    } catch {
      window.open(url, '_self');
    }
  };

  return (
    <div className="mt-3 space-y-2" data-testid="ai-provider-suggestions">
      {query && (
        <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
          <Calendar className="w-3 h-3" />
          <span>
            {query.service} · {query.date} · {query.time} · {query.gender === 'female' ? '♀' : '♂'}
          </span>
        </div>
      )}
      {providers.map((p) => (
        <div
          key={p.id}
          className="bg-white border border-gray-200 rounded-xl p-3 hover:border-blue-300 transition-colors"
          data-testid={`ai-provider-card-${p.id}`}
        >
          <div className="flex gap-3 items-center">
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0 flex items-center justify-center">
              {p.profile_image_url ? (
                <img src={absolute(p.profile_image_url)} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <Bot className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
              <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                {p.category && <span>{p.category}</span>}
                {p.area && (
                  <>
                    <span>·</span>
                    <span className="inline-flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />
                      {p.area}
                    </span>
                  </>
                )}
                {p.rating > 0 && (
                  <>
                    <span>·</span>
                    <span className="inline-flex items-center gap-0.5">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      {p.rating.toFixed(1)}
                    </span>
                  </>
                )}
              </div>
              {(p.matched_service_name || p.price != null) && (
                <p className="mt-1 text-[11px] font-semibold text-gray-700 numeric-ltr">
                  {p.matched_service_name}
                  {p.price != null && ` · ${p.price.toFixed(3)} KWD`}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => openBooking(p.book_url)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors flex-shrink-0"
              data-testid={`ai-book-btn-${p.id}`}
            >
              {p.is_slot_busy ? t('assistant.seeTimes', 'See times') : t('assistant.bookNow', 'Book now')}
            </button>
          </div>

          {p.free_times && p.free_times.length > 0 && (
            <div className="mt-2.5 pt-2.5 border-t border-gray-100" data-testid={`ai-free-times-${p.id}`}>
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">
                {t('assistant.freeTimes', 'Free times')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {p.free_times.map((slot) => (
                  <button
                    key={slot.time}
                    type="button"
                    onClick={() => openBooking(slot.book_url)}
                    data-testid={`ai-slot-${p.id}-${slot.time}`}
                    className="numeric-ltr px-2.5 py-1 text-[11px] font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                    title={slot.staff_name || ''}
                  >
                    {slot.time}
                  </button>
                ))}
              </div>
              {p.points_estimate > 0 && (
                <p className="mt-2 text-[10px] font-semibold text-emerald-700 numeric-ltr">
                  {t('assistant.earnPoints', 'Earn ~{{points}} points by booking online', { points: p.points_estimate })}
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initial greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'مرحباً! أنا عنايتك 👋\n\nHello! I\'m Enayatak, your care assistant. How can I help you find the perfect service today?\n\nكيف يمكنني مساعدتك اليوم؟',
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const params = new URLSearchParams({
        message: userMessage.content,
        session_id: sessionId || '',
        language: 'auto'
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-assistant/chat?${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        action: data.action || null,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('AI Assistant error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.\n\nSorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Detect if message is in Arabic
  const isArabic = (text) => {
    return /[\u0600-\u06FF]/.test(text);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-[calc(1.25rem+env(safe-area-inset-bottom))] end-4 sm:end-6 z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform md:hover:scale-105 transition-all duration-300 flex items-center gap-2 group"
        aria-label="Open AI Assistant"
        data-testid="ai-assistant-fab"
      >
        <Bot className="w-6 h-6" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out whitespace-nowrap">
          عنايتك | Enayatak
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] end-3 sm:bottom-6 sm:end-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col transition-all duration-300 ${
        isMinimized
          ? 'w-[min(18rem,calc(100vw-1.5rem))] h-14'
          : 'w-[calc(100vw-1.5rem)] sm:w-[26rem] h-[70vh] sm:h-[560px] max-h-[85vh]'
      }`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">عنايتك | Enayatak</h3>
            <p className="text-xs text-white/80">AI Assistant • مساعد ذكي</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label={isMinimized ? "Maximize" : "Minimize"}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : msg.error
                      ? 'bg-red-100 text-red-800 rounded-bl-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                  dir={isArabic(msg.content) ? 'rtl' : 'ltr'}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  {msg.role === 'assistant' && <ProviderSuggestions action={msg.action} />}
                  <p className={`text-xs mt-1 ${
                    msg.role === 'user' ? 'text-white/70' : 'text-gray-400'
                  }`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Thinking... | جاري التفكير</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length === 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {[
                { en: 'Find a ladies salon', ar: 'ابحث عن صالون نسائي' },
                { en: 'Find a barbershop', ar: 'ابحث عن صالون رجالي' },
                { en: 'Book a doctor', ar: 'احجز طبيب' },
                { en: 'Browse services', ar: 'تصفح الخدمات' }
              ].map((action, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInputMessage(action.en);
                    inputRef.current?.focus();
                  }}
                  className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors"
                >
                  {action.en} | {action.ar}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white rounded-b-2xl">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message... | اكتب رسالة..."
                data-testid="ai-assistant-input"
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                dir="auto"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center mt-2">
              Powered by AI • مدعوم بالذكاء الاصطناعي
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AIAssistant;
